import {useEffect, useState} from "react";
import Card from "@/components/Card";
import ProjectItem from "@/components/architecture/ProjectItem";
import ProjectItemCreate from "@/components/architecture/ProjectItemCreate";
import {useLoader} from "@/contexts/LoaderProvider";
import {useError} from "@/contexts/ErrorProvider";
import DashboardLayout from "@/components/layouts/Dashboard";
import SingleServiceItemCreate from "@/components/SingleServiceItemCreate";
import {apiGetProjectList, ProjectCreationResponse} from "@/services/ProjectService";
import {shredsListExplanation} from "@/components/explanations/ProjectsExplanation";
import ExplanationBox from "@/components/ExplanationBox.tsx";
import {Brain, Package, Sparkles, Calendar, CheckCircle, Clock, AlertCircle, ArrowRight, Layers, Target, Play, XCircle, Wrench} from "lucide-react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

// Extended type for the new API response
interface ExtendedProjectResponse extends Omit<ProjectCreationResponse, 'createdDate' | 'projectType' | 'demo'> {
  totalShreds?: number;
  finishedShreds?: number;
  inProgressShreds?: number;
  failedShreds?: number;
  projectType?: 'PROJECT' | 'SINGLE_SERVICE';
  demo?: boolean;
  createdDate?: string;
}

type ProjectList = ExtendedProjectResponse[]

const Architecture = () => {
  const {setIsLoading} = useLoader();

  const {setIsError} = useError();

  const [projects, setProjects] = useState<ProjectList>([]);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const response = await apiGetProjectList<ProjectList>();
      if (response.data) {
        setIsError('');

        // Filter out demo projects and sort by creation date (latest first)
        const filteredProjects = response.data.filter(project => !project.demo);
        const sortedProjects = filteredProjects.sort((a, b) => {
          if (!a.createdDate || !b.createdDate) return 0;
          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        });

        setProjects(sortedProjects);
      }
    } catch (errors: any) {
      setIsError(errors?.response?.data?.message || errors.toString());
    }

    setIsLoading(false);
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'GENERATED':
        return {
          color: 'text-emerald-700',
          bg: 'bg-emerald-100',
          border: 'border-emerald-300',
          icon: <CheckCircle className="w-4 h-4" />,
          label: 'Active'
        };
      case 'NOT_GENERATED':
        return {
          color: 'text-amber-700',
          bg: 'bg-amber-100',
          border: 'border-amber-300',
          icon: <Clock className="w-4 h-4" />,
          label: 'Processing'
        };
      default:
        return {
          color: 'text-gray-700',
          bg: 'bg-gray-100',
          border: 'border-gray-300',
          icon: <AlertCircle className="w-4 h-4" />,
          label: 'Inactive'
        };
    }
  };

  const getProjectTypeConfig = (projectType: string) => {
    switch (projectType) {
      case 'PROJECT':
        return {
          color: 'text-purple-700',
          bg: 'bg-purple-100',
          border: 'border-purple-300',
          icon: <Layers className="w-4 h-4" />,
          label: 'Project'
        };
      case 'SINGLE_SERVICE':
        return {
          color: 'text-indigo-700',
          bg: 'bg-indigo-100',
          border: 'border-indigo-300',
          icon: <Wrench className="w-4 h-4" />,
          label: 'Single Service'
        };
      default:
        return {
          color: 'text-gray-700',
          bg: 'bg-gray-100',
          border: 'border-gray-300',
          icon: <Package className="w-4 h-4" />,
          label: 'Unknown'
        };
    }
  };

  const getShredStatsConfig = () => {
    return {
      total: {
        color: 'text-blue-700',
        bg: 'bg-blue-100',
        border: 'border-blue-300',
        icon: <Target className="w-4 h-4" />,
        label: 'Total'
      },
      finished: {
        color: 'text-green-700',
        bg: 'bg-green-100',
        border: 'border-green-300',
        icon: <CheckCircle className="w-4 h-4" />,
        label: 'Done'
      },
      inProgress: {
        color: 'text-yellow-700',
        bg: 'bg-yellow-100',
        border: 'border-yellow-300',
        icon: <Play className="w-4 h-4" />,
        label: 'Active'
      },
      failed: {
        color: 'text-red-700',
        bg: 'bg-red-100',
        border: 'border-red-300',
        icon: <XCircle className="w-4 h-4" />,
        label: 'Failed'
      }
    };
  };

  return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto flex flex-col items-stretch gap-5 h-full w-full grow">
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 rounded-2xl p-4 sm:p-6 text-white">
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="absolute top-0 right-0 w-32 h-32 sm:w-96 sm:h-96 bg-gradient-to-bl from-white/10 to-transparent rounded-full -translate-y-16 translate-x-16 sm:-translate-y-48 sm:translate-x-48"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 sm:w-64 sm:h-64 bg-gradient-to-tr from-white/5 to-transparent rounded-full translate-y-12 -translate-x-12 sm:translate-y-32 sm:-translate-x-32"></div>

            <div className="relative z-10">
              {/* Mobile Layout - Stack vertically */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                    <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold mb-1">AI Projects</h1>
                    <p className="text-white/80 text-sm sm:text-base">Manage and monitor your intelligent projects</p>
                  </div>
                </div>
                
                {/* Stats - Stack on mobile, horizontal on larger screens */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-6 text-xs sm:text-sm">
                  <div className="flex items-center gap-1 sm:gap-2 bg-white/10 backdrop-blur-sm px-2 sm:px-4 py-1 sm:py-2 rounded-lg">
                    <Package className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{projects.length} Projects</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 bg-white/10 backdrop-blur-sm px-2 sm:px-4 py-1 sm:py-2 rounded-lg">
                    <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{projects.filter(p => p.specsStatus === 'GENERATED').length} Active</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ExplanationBox
              title="Your First Step"
              content={shredsListExplanation}
              boxWidth="md:w-full"
              boxHeight="h-full"
          />
          <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="full" grow>
            <div className="p-3 sm:p-4 xl:p-5 grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 h-full w-full">
              <ProjectItemCreate/>
              <SingleServiceItemCreate/>
            </div>
            <div className="p-3 sm:p-4 xl:p-5 grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 h-full w-full">
              {projects.map((project, index) => {
                const statusConfig = getStatusConfig(project.specsStatus);
                const projectTypeConfig = getProjectTypeConfig(project.projectType || '');
                const shredStats = getShredStatsConfig();

                // Check if project has new shred data to decide which component to render
                const hasShredData = project.totalShreds !== undefined ||
                    project.finishedShreds !== undefined ||
                    project.inProgressShreds !== undefined ||
                    project.failedShreds !== undefined;

                if (hasShredData) {
                  const link = project.specsStatus === "NOT_GENERATED"
                      ? `/architecture/specs/${project.id}`
                      : `/architecture/preview/${project.id}`;

                  // Render enhanced project card with shred statistics
                  return (
                      <Link
                          key={index}
                          to={link}
                          className={`bg-white border-2 border-gray-200 rounded-xl shadow-md hover:shadow-lg hover:border-gray-300 transition-all duration-300 cursor-pointer group overflow-hidden block ${
                              project.demo ? "ring-2 ring-blue-400" : ""
                          }`}
                      >
                        {/* Header with Status Badge */}
                        <div className="bg-gray-100 border-b border-gray-200 p-3 sm:p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-800 truncate flex-1 min-w-0">
                              {project.projectName || 'Untitled Project'}
                            </h3>

                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Status Badge in Header */}
                              <div className={`inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 ${statusConfig.bg} ${statusConfig.border} border rounded-full shadow-sm`}>
                                {statusConfig.icon}
                                <span className={`text-xs font-semibold ${statusConfig.color}`}>
                                {statusConfig.label}
                              </span>
                              </div>

                              {project.demo && (
                                  <div className="bg-blue-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-semibold">
                                    DEMO
                                  </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="p-3 sm:p-4 bg-white">
                          {/* Project Info Badges */}
                          <div className="flex flex-wrap gap-1 sm:gap-2 mb-3">
                            <div className="bg-gray-50 border border-gray-200 rounded-full px-2 sm:px-3 py-1 shadow-sm">
                              <div className="flex items-center gap-1 sm:gap-2">
                                <Package className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 flex-shrink-0" />
                                <span className="text-xs font-medium text-gray-700 truncate">
                                {project.mainPackage || 'N/A'}
                              </span>
                              </div>
                            </div>

                            <div className="bg-gray-50 border border-gray-200 rounded-full px-2 sm:px-3 py-1 shadow-sm">
                              <div className="flex items-center gap-1 sm:gap-2">
                                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 flex-shrink-0" />
                                <span className="text-xs font-medium text-gray-700">
                                {project.createdDate ? dayjs(project.createdDate).format('MMM DD, YYYY') : 'Unknown'}
                              </span>
                              </div>
                            </div>

                            {/* Project Type Badge */}
                            <div className={`${projectTypeConfig.bg} ${projectTypeConfig.border} border rounded-full px-2 sm:px-3 py-1 shadow-sm`}>
                              <div className="flex items-center gap-1 sm:gap-2">
                                {projectTypeConfig.icon}
                                <span className={`text-xs font-medium ${projectTypeConfig.color}`}>
                                {projectTypeConfig.label}
                              </span>
                              </div>
                            </div>
                          </div>

                          {/* Separator */}
                          <div className="border-t border-gray-200 my-3"></div>

                          {/* Shred Statistics Badges */}
                          <div className="mb-3 sm:mb-4">
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Shred Statistics</h4>
                            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1 sm:gap-2">
                              {/* Total Shreds Badge */}
                              <div className={`${shredStats.total.bg} ${shredStats.total.border} border rounded-full px-2 sm:px-3 py-1 sm:py-2 shadow-sm`}>
                                <div className="flex items-center gap-1 sm:gap-2">
                                  {shredStats.total.icon}
                                  <span className={`text-xs font-semibold ${shredStats.total.color} truncate`}>
                                  {shredStats.total.label}: {project.totalShreds || 0}
                                </span>
                                </div>
                              </div>

                              {/* Finished Shreds Badge */}
                              <div className={`${shredStats.finished.bg} ${shredStats.finished.border} border rounded-full px-2 sm:px-3 py-1 sm:py-2 shadow-sm`}>
                                <div className="flex items-center gap-1 sm:gap-2">
                                  {shredStats.finished.icon}
                                  <span className={`text-xs font-semibold ${shredStats.finished.color} truncate`}>
                                  {shredStats.finished.label}: {project.finishedShreds || 0}
                                </span>
                                </div>
                              </div>

                              {/* In Progress Shreds Badge */}
                              <div className={`${shredStats.inProgress.bg} ${shredStats.inProgress.border} border rounded-full px-2 sm:px-3 py-1 sm:py-2 shadow-sm`}>
                                <div className="flex items-center gap-1 sm:gap-2">
                                  {shredStats.inProgress.icon}
                                  <span className={`text-xs font-semibold ${shredStats.inProgress.color} truncate`}>
                                  {shredStats.inProgress.label}: {project.inProgressShreds || 0}
                                </span>
                                </div>
                              </div>

                              {/* Failed Shreds Badge */}
                              <div className={`${shredStats.failed.bg} ${shredStats.failed.border} border rounded-full px-2 sm:px-3 py-1 sm:py-2 shadow-sm`}>
                                <div className="flex items-center gap-1 sm:gap-2">
                                  {shredStats.failed.icon}
                                  <span className={`text-xs font-semibold ${shredStats.failed.color} truncate`}>
                                  {shredStats.failed.label}: {project.failedShreds || 0}
                                </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action indicator */}
                          <div className="flex items-center justify-end">
                            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all duration-200" />
                          </div>
                        </div>
                      </Link>
                  );
                } else {
                  // Render original ProjectItem component for projects without shred data
                  return (
                      <ProjectItem
                          id={project.id}
                          key={index}
                          name={project.projectName}
                          description={project.mainPackage}
                          specsStatus={project.specsStatus}
                          progress={100}
                          classColor={"progress-purple"}
                          className={`${project.demo ? "demoProject" : ""}`}
                      />
                  );
                }
              })}
            </div>
          </Card>
        </div>

      </DashboardLayout>
  );
};

export default Architecture;